import React, { useState } from "react";

import ExplainDiff from "components/ExplainDiff";
import {
  ComparablePlanSummaryType,
  ComparablePlanType,
  useCurrentComparePlan,
  useCurrentPlan,
} from "components/WithExplainPlan";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit } from "@fortawesome/pro-solid-svg-icons";
import { faCog } from "@fortawesome/pro-light-svg-icons";
import Grid, { MsCell } from "components/Grid";
import ModalContainer from "components/ModalContainer";
import { mru } from "utils/array";
import Panel from "components/Panel";
import PanelSection from "components/PanelSection";
import Button from "components/Button";
import { Link, useLocation } from "react-router-dom";
import ExplainFingerprint from "components/ExplainFingerprint";
import { formatMs } from "utils/format";
import classNames from "classnames";

import {
  WithExplainAspect,
  initialExplainAspect,
} from "components/WithExplainAspect";
import ExplainSelectAspect from "components/ExplainSelectAspect";

export const ExplainComparison = ({
  comparablePlans,
  linkToComparison,
  blockSize,
}: {
  comparablePlans: ComparablePlanSummaryType[];
  linkToComparison: (planA: string, planB?: string) => string;
  blockSize: number;
}) => {
  const planA = useCurrentPlan();
  const planB = useCurrentComparePlan();

  const hasCompareOptions = comparablePlans.length >= 2;
  // If we don't have both plans selected, but we do have options, open the
  // modal immediately
  const [open, setOpen] = useState(hasCompareOptions && (!planA || !planB));

  function toggleOpen() {
    setOpen((open) => !open);
  }
  const diffPlanA = planA && { label: planA.label, plan: planA.plan.plan };
  const diffPlanB = planB && { label: planB.label, plan: planB.plan.plan };

  return (
    <div>
      <WithExplainAspect
        initialAspect={initialExplainAspect(planA?.plan, planB?.plan)}
      >
        <Panel
          className={
            "mt-[1px]" /* TODO: On Firefox, the top border of the panel is cut off without an extra margin
        here because we're using box-shadow to draw the border; this should not be necessary. */
          }
          title="Plan Comparison"
          secondaryTitle={
            hasCompareOptions && (
              <Button bare variant="link" onClick={toggleOpen}>
                <FontAwesomeIcon icon={faCog} /> Select plans
              </Button>
            )
          }
        >
          <PanelSection>
            {(!planA || !planB) &&
              (hasCompareOptions ? (
                <div>
                  <FontAwesomeIcon
                    icon={faEdit}
                    className="text-[#31708f] mr-1"
                  />
                  <Button bare onClick={toggleOpen} variant="link">
                    Select plans
                  </Button>{" "}
                  to continue
                </div>
              ) : (
                <div>Comparison requires at least two recorded plans</div>
              ))}
            {open && (
              <SelectPlansToCompareModal
                toggleOpen={toggleOpen}
                comparablePlans={comparablePlans}
                planA={planA}
                planB={planB}
                linkToComparison={linkToComparison}
              />
            )}
            {planA && planB && (
              <>
                <ExplainSelectAspect label="Compare" showSubAspects />
                <ExplainDiff
                  planA={diffPlanA}
                  planB={diffPlanB}
                  blockSize={blockSize}
                />
              </>
            )}
          </PanelSection>
        </Panel>
      </WithExplainAspect>
    </div>
  );
};

export function useComparisonPlanIds() {
  const { search } = useLocation();
  const searchParams = new URLSearchParams(search);
  const planA = searchParams.get("planA");
  const planB = searchParams.get("planB");

  return [planA, planB] as const;
}

export function useComparisonPlans(
  comparablePlans: ComparablePlanType[] | undefined,
) {
  const { search } = useLocation();
  if (!comparablePlans) {
    return undefined;
  }
  const searchParams = new URLSearchParams(search);
  const planAId = searchParams.get("planA");
  const planBId = searchParams.get("planB");

  const planA = comparablePlans.find((p) => p.id === planAId);
  const planB = comparablePlans.find((p) => p.id === planBId);

  return [planA, planB];
}

function SelectPlansToCompareModal({
  comparablePlans,
  toggleOpen,
  linkToComparison,
  planA,
  planB,
}: {
  comparablePlans: ComparablePlanSummaryType[];
  toggleOpen: () => void;
  linkToComparison: (planA: string, planB?: string) => string;
  planA: ComparablePlanType;
  planB: ComparablePlanType;
}) {
  const initialCandidates = [] as string[];
  if (planA) {
    initialCandidates.push(planA.id);
  }
  if (planB) {
    initialCandidates.push(planB.id);
  }
  const [compareCandidates, setCompareCandidates] = useState(initialCandidates);

  function addCompareCanditate(candidateId: string) {
    const newCandidates = mru(candidateId, compareCandidates, 2);
    setCompareCandidates(newCandidates);
  }

  function clearCompareCanditate(candidateId: string) {
    const newCandidates = compareCandidates.filter((id) => id !== candidateId);
    setCompareCandidates(newCandidates);
  }

  let candidateA: ComparablePlanSummaryType,
    candidateB: ComparablePlanSummaryType;
  if (compareCandidates.length > 0) {
    candidateA = comparablePlans.find((p) => p.id === compareCandidates[0]);
  }
  if (compareCandidates.length > 1) {
    // If the user selects two plans, we want the oldest selection to be Plan A
    // and the most recent to be Plan B (we expect this is more intuitive).
    // Since the result of `mru` is reversed (it's sorted by recency), we need
    // to switch these:
    candidateB = candidateA;
    candidateA = comparablePlans.find((p) => p.id === compareCandidates[1]);
  }

  const comparisonLink =
    candidateA && candidateB
      ? linkToComparison(candidateA.id, candidateB.id)
      : "";

  return (
    <ModalContainer
      title="Select plans to compare"
      layout="centered"
      onClose={toggleOpen}
    >
      <Grid
        pageSize={10}
        data={comparablePlans}
        columns={[
          {
            field: "id",
            header: "",
            disableSort: true,
            width: "34px",
            renderer: ({ fieldData }) => {
              function handleToggleChecked(
                e: React.ChangeEvent<HTMLInputElement>,
              ) {
                if (e.currentTarget.checked) {
                  addCompareCanditate(fieldData);
                } else {
                  clearCompareCanditate(fieldData);
                }
              }
              const checked = compareCandidates.includes(fieldData);
              return (
                <input
                  type="checkbox"
                  className="cursor-pointer !mt-0.5"
                  checked={checked}
                  onChange={handleToggleChecked}
                />
              );
            },
          },
          {
            field: "fingerprint",
            width: "120px",
            renderer: ({ fieldData }) => {
              return <ExplainFingerprint fingerprint={fieldData} />;
            },
          },
          {
            width: "1fr",
            field: "label",
          },
          {
            field: "runtime",
            style: "number",
            nullValue: "n/a",
            renderer: MsCell,
            width: "minmax(10%,180px)",
          },
          {
            field: "ioMs",
            header: "I/O Read Time",
            style: "number",
            nullValue: "n/a",
            renderer: MsCell,
            width: "minmax(10%,180px)",
          },
        ]}
      />
      <div className="mt-4">
        <PlanSummaryTable planA={candidateA} planB={candidateB} />
      </div>
      <Link
        className={classNames(
          "mt-4 btn btn-success",
          !comparisonLink && "disabled pointer-events-none",
        )}
        onClick={comparisonLink ? toggleOpen : undefined}
        to={comparisonLink}
      >
        Compare Plans
      </Link>
    </ModalContainer>
  );
}

function PlanSummaryTable({
  planA,
  planB,
}: {
  planA: ComparablePlanSummaryType;
  planB: ComparablePlanSummaryType;
}) {
  return (
    <table className="w-full border-spacing-1 border-separate table-fixed">
      <thead>
        <tr>
          <th className="w-16"></th>
          <th className="w-32">Fingerprint</th>
          <th className="w-full">Description</th>
          <th className="w-32 text-right">Runtime</th>
          <th className="w-32 text-right">I/O Read Time</th>
        </tr>
      </thead>
      <tbody>
        <PlanSummaryRow label="Plan A" plan={planA} />
        <PlanSummaryRow label="Plan B" plan={planB} />
      </tbody>
    </table>
  );
}

function PlanSummaryRow({
  label,
  plan,
}: {
  label: string;
  plan: ComparablePlanSummaryType;
}) {
  return (
    <tr>
      <th className="whitespace-nowrap h-6" scope="row">
        {label}
      </th>
      <td>{plan ? <ExplainFingerprint explain={plan} /> : "n/a"}</td>
      <td>{plan?.label ?? "n/a"}</td>
      <td className="text-right">
        {plan?.runtime != null ? formatMs(plan.runtime) : "n/a"}
      </td>
      <td className="text-right">
        {plan?.ioMs != null ? formatMs(plan.ioMs) : "n/a"}
      </td>
    </tr>
  );
}

export default ExplainComparison;
